import React, { useMemo } from 'react';
import { Activity, Calendar, RefreshCw, AlertTriangle } from 'lucide-react';
import { useVisitTrendsData, TrendInterval, type VisitTrendPoint } from '../../features/clients/hooks/useVisitTrendsData';
import { Skeleton } from '../ui/Skeleton';

// Interfaz para datos formateados (sin cambios)
interface FormattedTrendData {
  label: string;
  count: number;
}

const VisitsTrendsPanel: React.FC = () => {
  // Hook (sin cambios aquí)
  const {
    aggregatedData,
    loading,
    error,
    refetchTrendData,
    interval, // Usamos el intervalo del hook
    changeInterval
  } = useVisitTrendsData('day');

  // Formateo de etiquetas (sin cambios aquí)
  const formattedChartData = useMemo((): FormattedTrendData[] => {
     // ... (lógica de formatLabel y mapeo sin cambios) ...
     if (!aggregatedData) return [];

    const formatLabel = (dateString: string, currentInterval: TrendInterval): string => {
      try {
        const dateParts = dateString.split('-');
        if (dateParts.length !== 3) return dateString;
        const date = new Date(Date.UTC(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2])));
        if (isNaN(date.getTime())) return dateString;

        if (currentInterval === 'day') {
          return date.toLocaleDateString('es-ES', { timeZone: 'UTC', weekday: 'short', day: '2-digit' });
        } else if (currentInterval === 'week') {
           const weekStart = new Date(date);
           const dayOfWeek = weekStart.getUTCDay(); // Usar getUTCDay con fecha UTC
           const diff = weekStart.getUTCDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
           weekStart.setUTCDate(diff); // Usar setUTCDate
           return `Sem ${weekStart.toLocaleDateString('es-ES', { timeZone: 'UTC', day: '2-digit', month: '2-digit' })}`;
        } else if (currentInterval === 'month') {
          return date.toLocaleDateString('es-ES', { timeZone: 'UTC', month: 'short', year: 'numeric' });
        }
        return dateString;
      } catch (e) {
        console.error("Error formatting date label:", dateString, e);
        return dateString;
      }
    };
    return aggregatedData.map((dataPoint: VisitTrendPoint) => ({
      label: formatLabel(dataPoint.date, interval),
      count: dataPoint.count
    }));
  }, [aggregatedData, interval]);

  // Cálculo de tendencia (sin cambios aquí)
  const trend = useMemo(() => {
     // ... (lógica sin cambios) ...
       if (formattedChartData.length < 2) return { percentage: 0, isUp: false };
    const midPoint = Math.floor(formattedChartData.length / 2);
    if (midPoint === 0) return { percentage: 0, isUp: false };
    const firstHalf = formattedChartData.slice(0, midPoint);
    const secondHalf = formattedChartData.slice(midPoint);
    const firstHalfAvg = firstHalf.reduce((sum, item) => sum + item.count, 0) / firstHalf.length || 0;
    const secondHalfAvg = secondHalf.reduce((sum, item) => sum + item.count, 0) / secondHalf.length || 0;
    if (firstHalfAvg === 0) {
        return { percentage: secondHalfAvg > 0 ? 100 : 0, isUp: secondHalfAvg > 0 };
    }
    const percentChange = ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100;
    return { percentage: Math.abs(Math.round(percentChange)), isUp: percentChange >= 0 };
  }, [formattedChartData]);

  // Cálculo de maxVisits (sin cambios aquí)
  const maxVisits = Math.max(...formattedChartData.map(d => d.count), 0);


  // --- RENDERIZADO ---
  return (
    <div className="bg-surface overflow-hidden shadow rounded-lg divide-y divide-line">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        {/* Título (sin cambios) */}
        <h3 className="text-lg font-medium text-content flex items-center">
          <Activity className="mr-2 h-5 w-5 text-blue-500" />
          Tendencia de Visitas
        </h3>
         {/* Botones de periodo (llaman a changeInterval, sin cambios aquí) */}
        <div className="flex space-x-1">
          <button onClick={() => changeInterval('day')}   className={`px-2 py-1 text-xs font-medium rounded ${ interval === 'day'   ? 'bg-accent/10 text-accent' : 'text-content-2 hover:bg-canvas' }`}>Diario</button>
          <button onClick={() => changeInterval('week')}  className={`px-2 py-1 text-xs font-medium rounded ${ interval === 'week'  ? 'bg-accent/10 text-accent' : 'text-content-2 hover:bg-canvas' }`}>Semanal</button>
          <button onClick={() => changeInterval('month')} className={`px-2 py-1 text-xs font-medium rounded ${ interval === 'month' ? 'bg-accent/10 text-accent' : 'text-content-2 hover:bg-canvas' }`}>Mensual</button>
        </div>
      </div>

      {/* Estado de Carga o Error (sin cambios) */}
      {loading ? (
         <div className="px-4 py-5 sm:p-6" style={{ height: '250px' }}>
           <div className="flex items-center justify-between mb-3">
             <Skeleton className="h-4 w-32" />
             <Skeleton className="h-4 w-28" />
           </div>
           <div className="flex items-end justify-around gap-1" style={{ height: '180px' }}>
             {[55, 90, 45, 130, 75, 150, 65, 110, 85, 120].map((h, i) => (
               <Skeleton key={i} className="w-10 md:w-12 rounded-t-md" style={{ height: `${h}px` }} />
             ))}
           </div>
         </div>
      ) : error ? (
          <div className="px-4 py-5 sm:p-6 flex flex-col justify-center items-center text-center text-red-600 dark:text-red-400" style={{ height: '250px' }}>
            <AlertTriangle className="h-8 w-8 mb-2" />
            <p className="font-medium">Error al cargar datos</p>
            <p className="text-sm mt-1">{error}</p>
            <button
              onClick={refetchTrendData}
              className="mt-4 px-3 py-1 border border-red-300 text-xs font-medium rounded text-red-700 dark:text-red-300 bg-surface hover:bg-red-50 dark:hover:bg-red-500/10"
            >
              Reintentar
            </button>
          </div>
       ) : (
        // --- Mostrar contenido si hay datos ---
        <>
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-content-3 mr-1" />
                {/* --- CAMBIO: Etiqueta de rango dinámica --- */}
                <span className="text-sm text-content-3">
                  {interval === 'day' ? 'Últimos 15 días' : interval === 'week' ? 'Últimas 8 semanas' : 'Últimos 6 meses'}
                </span>
                {/* --- FIN CAMBIO --- */}
              </div>
              <div className="flex items-center">
                 {/* Tendencia y botón Refrescar (sin cambios aquí) */}
                 <div className={`text-sm font-medium flex items-center ${trend.isUp ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                   {trend.isUp ? ( <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg> )
                               : ( <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg> )}
                   {trend.percentage}% {trend.isUp ? 'más' : 'menos'} visitas
                 </div>
                <button
                  onClick={refetchTrendData}
                  disabled={loading}
                  className="ml-2 text-accent hover:text-blue-800 dark:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Actualizar datos de tendencias"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Gráfico de Barras (sin cambios aquí, usa formattedChartData) */}
            <div className="relative" style={{ height: '200px' }}>
              {formattedChartData.length > 0 ? (
                <div className="absolute inset-0 flex items-end justify-around space-x-1">
                  {formattedChartData.map((data, index) => (
                    <div key={data.label + '-' + index} className="flex flex-col items-center justify-end h-full">
                      <div
                        className="w-10 md:w-12 bg-blue-500 hover:bg-blue-600 transition-all duration-300 ease-in-out rounded-t-md flex items-center justify-center text-white text-xs font-medium"
                        style={{
                          height: `${maxVisits > 0 ? Math.max(0, (data.count / maxVisits) * 150) : 0}px`,
                          minHeight: data.count > 0 ? '20px' : '0px'
                        }}
                        title={`${data.label}: ${data.count} visita${data.count !== 1 ? 's' : ''}`}
                      >
                        {(data.count / maxVisits) * 150 >= 20 ? data.count : ''}
                      </div>
                      <div className="mt-1 text-[10px] md:text-xs text-content-3 text-center whitespace-nowrap">{data.label}</div>
                    </div>
                  ))}
                </div>
              ) : (
                 <div className="absolute inset-0 flex items-center justify-center text-content-3">
                    No hay visitas en este período.
                 </div>
              )}
              <div className="absolute bottom-5 left-0 right-0 h-px bg-line"></div>
            </div>
          </div>

          {/* Resumen de Tendencia (sin cambios) */}
          <div className="px-4 py-4 sm:px-6 bg-surface-2">
              <div className="text-xs text-content-3">
                  <p><span className="font-medium">Resumen:</span> {trend.isUp ? `Tus visitas están aumentando con un ${trend.percentage}% más que en el periodo anterior.` : trend.percentage > 0 ? `Tus visitas han disminuido un ${trend.percentage}% comparado con el periodo anterior.` : 'No hay suficiente información para calcular la tendencia.'}</p>
              </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VisitsTrendsPanel;